import React from "react";
import { view } from "../../lib/vvm";
import styles from "./Search.module.css";
import Icon from "../Icon/Icon";
import Button, { ButtonDesign } from "../Button/Button";
import Input from "../Input/Input";
import { SearchViewModel } from "./SearchViewModel";

interface SearchProps {}

export const Search: React.FC<SearchProps> = view(SearchViewModel)(({
  viewModel: {
    searchText,
    measureFilter,
    placementFilter,
    typeFilter,
    typeOptions,
    hasActiveFilters,
    hasSearchQuery,
    canReset,
    quickPresets,
    savedFilterPresets,
    canSavePreset,
    measureOptions,
    placementOptions,
    onChange,
    onFocus,
    onBlur,
    onMeasureFilterChange,
    onPlacementFilterChange,
    onTypeFilterChange,
    clearFilters,
    applyQuickPreset,
    applySavedPreset,
    removeSavedPreset,
    saveCurrentPreset,
    isQuickPresetActive,
    isSavedPresetActive,
  },
}) => {
  const onPanelFocusCapture = (): void => {
    onFocus();
  };

  const onPanelBlurCapture = (
    event: React.FocusEvent<HTMLDivElement>,
  ): void => {
    const nextFocused = event.relatedTarget;
    if (
      nextFocused instanceof Node &&
      event.currentTarget.contains(nextFocused)
    ) {
      return;
    }

    onBlur();
  };

  return (
    <div className={styles.root}>
      <div
        className={styles.panel}
        onFocusCapture={onPanelFocusCapture}
        onBlurCapture={onPanelBlurCapture}
      >
        <div className={styles.metaRow}>
          <strong className={styles.metaTitle}>Поиск по складу</strong>
          <span
            className={`${styles.metaBadge} ${
              hasActiveFilters ? styles.metaBadgeActive : ""
            }`}
          >
            {hasActiveFilters
              ? "Фильтры включены"
              : hasSearchQuery
                ? "Поиск по тексту"
                : "Без фильтров"}
          </span>
        </div>

        <div className={styles.searchLine}>
          <Input
            className={styles.input}
            placeholder="Найти товар или коробку"
            aria-label="Найти товар или коробку"
            onChange={onChange}
            value={searchText}
          />
          <Button
            type="button"
            className={styles.iconButton}
            aria-label="Искать"
          >
            <Icon name={"Search"} />
          </Button>
        </div>

        <div className={styles.filters}>
          <label className={styles.filterField}>
            <span className={styles.filterLabel}>Единицы</span>
            <select
              className={styles.filterSelect}
              value={measureFilter}
              onChange={onMeasureFilterChange}
              aria-label="Фильтр по единице измерения"
            >
              {measureOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.filterField}>
            <span className={styles.filterLabel}>Размещение</span>
            <select
              className={styles.filterSelect}
              value={placementFilter}
              onChange={onPlacementFilterChange}
              aria-label="Фильтр по размещению"
            >
              {placementOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.filterField}>
            <span className={styles.filterLabel}>Тип (#тег)</span>
            <select
              className={styles.filterSelect}
              value={typeFilter}
              onChange={onTypeFilterChange}
              aria-label="Фильтр по типу"
            >
              <option value="all">Все типы</option>
              {typeOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </label>

          <Button
            design={ButtonDesign.basic}
            className={styles.resetButton}
            onClick={clearFilters}
            disabled={!canReset}
          >
            Сбросить
          </Button>
        </div>

        <div className={styles.presets}>
          <div className={styles.presetsHeader}>
            <span className={styles.presetsTitle}>Быстрые пресеты</span>
            <Button
              design={ButtonDesign.ghost}
              className={styles.savePresetButton}
              onClick={saveCurrentPreset}
              disabled={!canSavePreset}
            >
              Сохранить текущий
            </Button>
          </div>

          <div className={styles.quickPresetsRow}>
            {quickPresets.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className={`${styles.presetChip} ${
                  isQuickPresetActive(preset.id) ? styles.presetChipActive : ""
                }`}
                onClick={() => applyQuickPreset(preset.id)}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {savedFilterPresets.length > 0 && (
            <div className={styles.savedPresetsRow}>
              {savedFilterPresets.map((preset) => (
                <div key={preset.id} className={styles.savedPresetItem}>
                  <button
                    type="button"
                    className={`${styles.presetChip} ${
                      isSavedPresetActive(preset.id)
                        ? styles.presetChipActive
                        : ""
                    }`}
                    onClick={() => applySavedPreset(preset.id)}
                  >
                    {preset.label}
                  </button>
                  <button
                    type="button"
                    className={styles.removePresetButton}
                    aria-label={`Удалить пресет ${preset.label}`}
                    onClick={() => removeSavedPreset(preset.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
